import React, { useState, useEffect, useMemo } from 'react'
import { WeatherData } from '../../types/ai'
import WeatherTrendsChart from '../../components/charts/WeatherTrendsChart'
import CorrelationScatterChart from '../../components/charts/CorrelationScatterChart'
import WindRoseChart from '../../components/charts/WindRoseChart'

const WeatherModel: React.FC = () => {
  const [weatherData, setWeatherData] = useState<WeatherData[]>([])
  const [currentWeather, setCurrentWeather] = useState<WeatherData | null>(null)
  const [correlationData, setCorrelationData] = useState<any[]>([])

  const mockWeatherData = useMemo(() => 
    Array.from({ length: 24 }, (_, i) => ({
      timestamp: new Date(Date.now() + i * 3600000).toISOString(),
      irradiance: 600 + i * 20 + (i % 3) * 50,
      temperature: 25 + (i % 8),
      wind_speed: 5 + (i % 4),
      humidity: 40 + (i % 20),
      cloud_coverage: 0.3
    })), []);

  const mockCurrentWeather = useMemo(() => ({
    timestamp: new Date().toISOString(),
    irradiance: 650,
    temperature: 28,
    wind_speed: 5.2,
    humidity: 45,
    cloud_coverage: 0.2
  }), []);

  const mockCorrelationData = useMemo(() => 
    Array.from({ length: 30 }, (_, i) => ({
      x: 300 + i * 20,
      y: 40 + i * 1.5 + (i % 3) * 10
    })), []);

  useEffect(() => {
    setWeatherData(mockWeatherData)
    setCurrentWeather(mockCurrentWeather)
    setCorrelationData(mockCorrelationData)
  }, [mockWeatherData, mockCurrentWeather, mockCorrelationData])



  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Weather Model</h1>
        <div className="flex gap-4">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Refresh Data
          </button>
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            Export Analysis
          </button>
        </div>
      </div>

      {/* Current Weather Overview */}
      {currentWeather && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-600 mb-2">Irradiance</h3>
            <p className="text-3xl font-bold bg-gradient-to-r from-yellow-600 to-yellow-500 bg-clip-text text-transparent">
              {Math.round(currentWeather.irradiance)} W/m²
            </p>
          </div>
          <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-600 mb-2">Temperature</h3>
            <p className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent">
              {currentWeather.temperature.toFixed(1)}°C
            </p>
          </div>
          <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-600 mb-2">Wind Speed</h3>
            <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
              {currentWeather.wind_speed.toFixed(1)} m/s
            </p>
          </div>
          <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-600 mb-2">Humidity</h3>
            <p className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">
              {Math.round(currentWeather.humidity)}%
            </p>
          </div>
          <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-600 mb-2">Cloud Cover</h3>
            <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-500 bg-clip-text text-transparent">
              {Math.round(currentWeather.cloud_coverage * 100)}%
            </p>
          </div>
        </div>
      )}

      {/* Weather Trends */}
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
          Weather Parameter Trends 🌤️
        </h3>
        <WeatherTrendsChart data={weatherData} />
        <p className="text-sm text-slate-500 mt-2">{weatherData.length} data points</p>
      </div>

      {/* Correlation Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            Irradiance vs Energy Correlation 🔄
          </h3>
          <CorrelationScatterChart data={correlationData} />
          <div className="mt-4 text-center">
            <span className="text-sm font-semibold text-slate-600 bg-green-100 px-3 py-1 rounded-full">
              R² = 0.87 (Strong Correlation)
            </span>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            Wind Impact Analysis 🌬️
          </h3>
          <WindRoseChart />
        </div>
      </div>

      {/* Weather Anomalies */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Weather Anomalies Detected</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Parameter
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Deviation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Flag
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  2025-01-08 14:30
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  Irradiance
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  -15%
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                    Low
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  2025-01-08 12:15
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  Wind Speed
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  +25%
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                    High
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default WeatherModel